import { AnyAction } from "redux"
import { Dispatch, RNBThunkAction } from "../store.config"
import axios from "axios"
import { MerchantCode } from "./merchantCodes.ducks"
import { Account } from "./accounts.ducks"
import { InvoiceTypeValues } from "../../utils/accountingPlan"
import { lockVATInstruction } from "./merchants.ducks"

export const enum AccountingPlanActionEnum {
  GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_ATTEMPT = "ACCOUNTING_PLAN/getInvoiceTypeInsctructionsAttempt",
  GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_SUCCESS = "ACCOUNTING_PLAN/getInvoiceTypeInsctructionsSuccess",
  GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_FAILURE = "ACCOUNTING_PLAN/getInvoiceTypeInsctructionsFailure",

  CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_ATTEMPT = "ACCOUNTING_PLAN/createUpdateInvoiceTypeInsctructionsAttempt",
  CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_SUCCESS = "ACCOUNTING_PLAN/createUpdateInvoiceTypeInsctructionsSuccess",
  CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_FAILURE = "ACCOUNTING_PLAN/createUpdateInvoiceTypeInsctructionsFailure",
}

export const getInvoiceTypeInsctructionsAttempt = () =>
  ({
    type: AccountingPlanActionEnum.GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_ATTEMPT,
  } as const)
export const getInvoiceTypeInsctructionsSuccess = (data: InvoiceTypeResponse) =>
  ({
    type: AccountingPlanActionEnum.GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_SUCCESS,
    data,
  } as const)
export const getInvoiceTypeInsctructionsFailure = () =>
  ({
    type: AccountingPlanActionEnum.GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_FAILURE,
  } as const)

export const createUpdateInvoiceTypeInsctructionsAttempt = () =>
  ({
    type: AccountingPlanActionEnum.CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_ATTEMPT,
  } as const)
export const createUpdateInvoiceTypeInsctructionsSuccess = () =>
  ({
    type: AccountingPlanActionEnum.CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "accounting-plan.typologies.tooltip.success.title",
      bodyKey: "accounting-plan.typologies.tooltip.success.message",
    },
  } as const)
export const createUpdateInvoiceTypeInsctructionsFailure = () =>
  ({
    type: AccountingPlanActionEnum.CREATE_UPDATE_INVOICE_TYPE_INSTRCUTIONS_TYPE_FAILURE,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: "accounting-plan.typologies.tooltip.error.title",
      bodyKey: "accounting-plan.typologies.tooltip.error.message",
    },
  } as const)

export type AccountingPlanActionType = ReturnType<
  | typeof getInvoiceTypeInsctructionsAttempt
  | typeof getInvoiceTypeInsctructionsSuccess
  | typeof getInvoiceTypeInsctructionsFailure
  | typeof createUpdateInvoiceTypeInsctructionsAttempt
  | typeof createUpdateInvoiceTypeInsctructionsSuccess
  | typeof createUpdateInvoiceTypeInsctructionsFailure
>

export interface AccountingPlanState {
  customization: {
    invoice_types: InvoiceTypes[]
    invoice_type_translations: { [index: string]: string }
    merchant_codes: MerchantCodeWithMerchant[]
    accounts: Account[]
  }
}

export const accountingPlanInitialState: AccountingPlanState = {
  customization: {
    invoice_types: [],
    invoice_type_translations: {},
    merchant_codes: [],
    accounts: [],
  },
}

export function accountingPlanReducer(
  state = accountingPlanInitialState,
  action: AccountingPlanActionType
): AccountingPlanState {
  switch (action.type) {
    case AccountingPlanActionEnum.GET_INVOICE_TYPE_INSTRCUTIONS_TYPE_SUCCESS: {
      return {
        ...state,
        customization: action.data,
      }
    }

    default:
      return state
  }
}

interface InvoiceTypes {
  account_id: number
  code: string
  invoice_type: InvoiceTypeType
  merchant_code_id: number
  number: string
  instruction_events: InstructionEvent[]
  lock_vat_instructions: lockVATInstruction
  vat_default_account_id: number | null
}

export interface InstructionEvent {
  created_at: string
  email: string
  metadata: {}
  event_type: TypologiesEventType
}

export type TypologiesEventType =
  | "add_merchant_code"
  | "update_merchant_code"
  | "add_account"
  | "update_account"
  | "edit_lock_vat_instructions"
  | "edit_vat_default_account_id"

export type InvoiceTypeType =
  | "restaurant_receipt"
  | "hotel_receipt"
  | "diesel_fuel_receipt"
  | "gasoline_fuel_receipt"
  | "toll_receipt"
  | "parking_receipt"
  | "taxi_receipt"
  | "other_receipt"

interface InvoiceTypeResponse {
  invoice_types: InvoiceTypes[]
  invoice_type_translations: { [index: string]: string }
  merchant_codes: MerchantCodeWithMerchant[]
  accounts: Account[]
  lock_vat_instructions: lockVATInstruction
  vat_default_account_id: number | null
}

interface MerchantCodeWithMerchant extends MerchantCode {
  merchant_name: string | null
}

export const getInvoiceTypeInsctructionsThunk =
  (companyId: number) => (dispatch: Dispatch<AnyAction>) => {
    dispatch(getInvoiceTypeInsctructionsAttempt())
    return axios
      .get<InvoiceTypeResponse>(`/companies/get_invoice_type_info`, {
        params: {
          id: companyId,
        },
      })
      .then(({ data }) => {
        dispatch(getInvoiceTypeInsctructionsSuccess(data))
      })
      .catch(() => {
        dispatch(getInvoiceTypeInsctructionsFailure())
      })
  }

export const createUpdateInvoiceTypeInsctructionsThunk =
  (companyId: number, invoice_type_values: InvoiceTypeValues) =>
  (dispatch: Dispatch<AnyAction | RNBThunkAction>) => {
    dispatch(createUpdateInvoiceTypeInsctructionsAttempt())
    return axios
      .post<InvoiceTypes>(`/companies/create_or_update_invoice_type`, {
        id: companyId,
        invoice_type_values: invoice_type_values,
      })
      .then(({ data }) => {
        dispatch(createUpdateInvoiceTypeInsctructionsSuccess())
        dispatch(getInvoiceTypeInsctructionsThunk(companyId))
      })
      .catch(() => {
        dispatch(createUpdateInvoiceTypeInsctructionsFailure())
      })
  }
